import {
  useNotificationGetUnreadCountQuery,
  useNotificationListInfiniteQuery,
  useNotificationUpdateAllReadMutation,
} from 'api/notification';
import { mergeNotificationPages } from 'pages';
import { useCallback, useMemo, useRef } from 'react';
import { useNotificationMessages } from 'hooks/useNotificationMessages';

export const useNotificationPage = () => {
  // const [unreadOnly, setUnreadOnly] = useState(false);
  const observerLastRef = useRef<IntersectionObserver | null>(null);
  const observerFirstRef = useRef<IntersectionObserver | null>(null);
  const userId = JSON.parse(localStorage.getItem('userId') || 'null');
  const { handleError } = useNotificationMessages();
  const [updateNotification] = useNotificationUpdateAllReadMutation();
  const { data: unreadCount } = useNotificationGetUnreadCountQuery(userId);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    isLoading,
  } = useNotificationListInfiniteQuery(
    {
      size: 15,
      userId,
    },
    {
      skip: !userId,
    }
  );
  const notifications = useMemo(() => {
    return mergeNotificationPages(data?.pages || []) || [];
  }, [data]);
  const handleAllReadChange = useCallback(async () => {
    try {
      const { error } = await updateNotification(userId);
      if (error) {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [handleError, updateNotification, userId]);

  const lastNotificationRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingNextPage) return;
      if (observerLastRef.current) observerLastRef.current.disconnect();
      observerLastRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );
      observerLastRef.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  );
  const firstNotificationRef = useCallback(
    (node: any) => {
      if (!node) return;
      if (isFetchingPreviousPage) return;
      if (observerFirstRef.current) observerFirstRef.current.disconnect();

      observerFirstRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasPreviousPage && !isFetchingPreviousPage) {
            fetchPreviousPage();
          }
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: 0,
        }
      );

      observerFirstRef.current.observe(node);
    },
    [isFetchingPreviousPage, hasPreviousPage, fetchPreviousPage]
  );
  return {
    // setUnreadOnly,
    unreadCount: unreadCount?.data,
    notifications,
    handleAllReadChange,
    firstNotificationRef,
    lastNotificationRef,
    isLoading,
    isFetchingNextPage,
  };
};
